import { StateAction } from "../../../utils/typeAlias";
import * as d3 from "d3";
import { StatSummary } from "../../../models/stat";
import HBScanStatService from "../../../services/stat/HBScanStatService";
import byteConvertUtilis from "../../../utils/byteConvertUtilis";
import { InteractiveMapBase } from "../InteractiveMap/interactiveMap";
import ToolProvider from "./tools/toolProvider";
import { StatToolTip } from "../InteractiveMap/toolTip";
import { Circle, ICircleCreationEvent } from "./edit/circle";
import { ETool } from "./tools/Etool";
import { theme } from "../../../styles/themes/mainTheme";

export const MAX_OVERTIME_MULTIPLIER = 1.5;

export class InteractiveStatMap extends InteractiveMapBase {
    private tooltip: StatToolTip;
    private circleRadius: number;
    private toolProvider: ToolProvider;
    private floorId: number;
    private saveCallBack: StateAction<number>;
    public static MIN_RADIUS = 6;
    public static DEFAULT_RADIUS = 6;
    public static MAX_RADIUS = 50;
    private toolBoxButtons: d3.Selection<
        HTMLButtonElement,
        any,
        HTMLElement,
        any
    >[];
    private toolBoxButtonsActions: Map<
        string,
        (button: d3.Selection<HTMLButtonElement, any, HTMLElement, any>) => void
    >;

    constructor(
        selector: d3.Selection<SVGElement, any, HTMLElement, any>,
        tooltip: StatToolTip,
        creationEvent: ICircleCreationEvent,
        floorId: number,
        saveCallback: StateAction<number>,
        toolBoxButtons: d3.Selection<HTMLButtonElement, any, HTMLElement, any>[]
    ) {
        super(selector);
        this.toolBoxButtons = toolBoxButtons;
        this.tooltip = tooltip;
        this.circleRadius =
            this.findMostFrequentRadius() || InteractiveStatMap.DEFAULT_RADIUS;

        this.toolProvider = ToolProvider.CreateInstance(
            tooltip,
            creationEvent,
            () => this.clearFadeTimeout()
        );
        this.floorId = floorId;
        this.saveCallBack = saveCallback;
        this.toolBoxButtonsActions = new Map([
            ["addButton", (button) => this.toggleTool(ETool.Pen, button)],
            ["deleteButton", (button) => this.toggleTool(ETool.Eraser, button)],
            ["saveButton", () => this.saveToolOperation()],
        ]);
    }

    private findMostFrequentRadius() {
        // Extract the radius values from the circle nodes
        const radii = this.circles
            ?.nodes()
            .map((circle: any) => Number(circle.attributes.r.value));
        if (!radii) return 0;

        // Count occurrences of each radius
        const radiusCount = radii.reduce(
            (countMap, radius) => {
                countMap[radius] = (countMap[radius] || 0) + 1;
                return countMap;
            },
            {} as Record<number, number>
        );

        let mostFrequentRadius = "0";
        let mostFrequentCount = 0;
        for (let radius in radiusCount) {
            const count = radiusCount[radius];

            if (count > mostFrequentCount) {
                mostFrequentRadius = radius;
                mostFrequentCount = count;
            }
        }

        return Number(mostFrequentRadius);
    }

    private setDefaultStyles() {
        const colors = Circle.colors;
        this.setCursor(this.toolProvider.toolType);

        this.circles?.each(function () {
            const circle = d3.select(this);
            const id = circle.attr("id");
            circle
                .style("fill", id ? colors.red : colors.grey)
                .attr("stroke-width", "0px");
        });
    }

    private setCursor(tool: ETool | undefined) {
        const toolType = tool || ETool.View;
        const defaultTool = ETool[ETool.View];
        this.svg.attr("class", Circle.cursors.get(toolType) || defaultTool);
    }

    private toggleTool(
        tool: ETool,
        button: d3.Selection<HTMLButtonElement, any, HTMLElement, any>
    ) {
        if (this.toolProvider.toolType === tool) {
            this.toolProvider.set(ETool.View);
            this.svg.attr("class", "view");
            return;
        }
        this.toolProvider.set(tool);
        this.setCursor(tool);
        button.style("color", theme.palette.primary.main);
    }

    private saveToolOperation() {
        this.toolProvider.set(ETool.View);
        this.zoomOut()
            .then(() => {
                const data = byteConvertUtilis.fromSvgToBlob(
                    this.svg.node() as Node
                );
                this.setCursor(ETool.Save);
                return HBScanStatService.saveMap(data, this.floorId);
            })
            .then(() => this.saveCallBack((prev) => prev + 1))
            .finally(() => this.setCursor(ETool.View));
    }

    private setToolBoxButtonHandlers() {
        this.toolBoxButtons.forEach((button) => {
            const id = button.attr("id");
            button.on("click", () => {
                this.toolBoxButtons.forEach(
                    // Setting tool's color to nothing so it resets to initial color. Setting it directly to a color breaks the hover effect !
                    (button) => button.style("color", "")
                );
                this.toolBoxButtonsActions.get(id)?.(button);
            });
        });
    }

    setRoomId(id: number) {
        return new Promise((res, err) => {
            const target = this.toolProvider.getTarget();
            if (!target) return err("no target");

            return res(target.setId(id));
        });
    }

    getCircleRadius() {
        return this.circleRadius;
    }

    setCircleRadius(radius: number) {
        this.circleRadius = radius;
    }

    init(
        stats: Map<number, StatSummary>,
        onClick: (id: number) => void,
        isOvertimeCalculation: boolean,
        isSuperAdmin: boolean = false
    ) {
        super.baseInit(this.tooltip);

        this.setDefaultStyles();
        if (isSuperAdmin) this.setToolBoxButtonHandlers();

        this.background.on(
            "click",
            (event: React.MouseEvent<HTMLButtonElement>) => {
                this.toolProvider.tool?.onClickBackground(event, {
                    background: this.svg,
                    circleRadius: this.circleRadius,
                });
                this.applyZoomTransform();
            }
        );
        const svg = this.svg;
        this.circles
            ?.datum(function () {
                const id = d3.select(this).attr("id");
                return id ? stats.get(Number(id.replace("cp", ""))) : undefined;
            })
            .each(function (stat) {
                const update = d3.select(this);
                const circle = Circle.fromD3(
                    svg,
                    update as unknown as d3.Selection<
                        SVGCircleElement,
                        any,
                        HTMLElement,
                        any
                    >
                );
                const color = (stat: StatSummary | undefined) => {
                    if (!stat || !stat.minMinutes || stat.isLocalLocked)
                        return Circle.colors.grey;

                    const nMinutes = Math.round(stat.minutes);

                    const isNotCleaned = nMinutes <= 0;
                    const isPartiallyCleaned = nMinutes < stat.minMinutes;
                    const isOverlyCleaned =
                        isOvertimeCalculation &&
                        nMinutes > stat.minMinutes * MAX_OVERTIME_MULTIPLIER;
                    const isCleaned = nMinutes >= stat.minMinutes;

                    if (isNotCleaned) return Circle.colors.red;
                    if (isPartiallyCleaned) return Circle.colors.orange;
                    if (isOverlyCleaned) return Circle.colors.purple;
                    if (isCleaned) return Circle.colors.green;
                    return Circle.colors.grey;
                };
                const isAnimationNeeded =
                    circle.select?.style("fill") !==
                    d3.rgb(color(stat)).toString();

                circle
                    .applyAnimations()
                    .changeColor(isAnimationNeeded, color(stat));

                circle.on(
                    "click",
                    (event: React.MouseEvent<HTMLButtonElement>) => {
                        const callback = stat
                            ? () => onClick(stat.roomId)
                            : () => {};

                        ToolProvider.GetInstance()?.tool?.onClickCircle(
                            circle,
                            event,
                            callback
                        );
                    }
                );

                circle.on(
                    "mouseover",
                    (event: React.MouseEvent<HTMLButtonElement>) => {
                        ToolProvider.GetInstance()?.tool?.onMouseOverCircle(
                            event,
                            stat
                        );
                    }
                );

                return update;
            });
    }
}
